import { Switch, Accordion, Select } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import type { FormValueTypes } from '~/common/notifications/types';

type SubFormProps = {
  form: UseFormReturnType<FormValueTypes>;
  showWeeklySummaryField: boolean;
};

type SummarySwitchProps = {
  form: UseFormReturnType<FormValueTypes>;
  field: 'weeklySummary' | 'dailySummary';
  label: string;
  description: string;
};

function SummarySwitch({ form, field, label, description }: SummarySwitchProps) {
  return (
    <div className="flex flex-row items-center gap-x-2 rounded-md border px-6 py-6 dark:border-gray-700">
      <Switch
        size="sm"
        {...form.getInputProps(field, { type: 'checkbox' })}
      />
      <div className="ml-3 flex flex-col">
        <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">{label}</div>
        <div className="whitespace-nowrap text-xs text-stone-500 dark:text-stone-400">{description}</div>
      </div>
    </div>
  );
}

export function NotificationSubForm({ form, showWeeklySummaryField }: SubFormProps) {
  return (
    <div className="mt-4 flex flex-col space-y-4">
      <div className="flex flex-row items-center gap-x-2 rounded-md border px-6 py-6 dark:border-gray-700">
        <Switch
          {...form.getInputProps('testRunStarts', { type: 'checkbox' })}
          size="sm"
        />
        <div className="ml-3 flex flex-col">
          <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">Test run starts</div>
          <div className="whitespace-nowrap text-xs text-stone-500 dark:text-stone-400">
            Whenever a new group run is triggered, whether from UI or with CI/CD integration
          </div>
        </div>
      </div>

      <div className="rounded-md border px-6 py-6 dark:border-gray-700">
        <Accordion
          styles={{
            control: {
              padding: 0,
              backgroundColor: 'transparent',
            },
            item: {
              borderBottom: 'none',
            },
          }}
          defaultValue="advanced-settings"
        >
          <Accordion.Item value="advanced-settings">
            <Accordion.Control>
              <div className="ml-0 flex flex-row items-center gap-x-2">
                <Switch
                  size="sm"
                  {...form.getInputProps('testRunFinishes', { type: 'checkbox' })}
                />
                <div className="ml-3 flex flex-col">
                  <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">
                    Test run finishes - {form.values.endType === 'FAILURES_ONLY' ? 'failures only' : 'all results'}
                  </div>
                  <div className="whitespace-nowrap text-xs text-stone-500 dark:text-stone-400">
                    {form.values.endType === 'FAILURES_ONLY' ?
                      'Only if the run fails'
                    : 'Whether the run result is pass or fail'}
                  </div>
                </div>
              </div>
            </Accordion.Control>
            <Accordion.Panel>
              <div className="mt-2 text-sm text-stone-500 dark:text-stone-400">Advanced settings</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="text-sm">Result type:</div>
                <Select
                  required
                  className="w-48"
                  {...form.getInputProps('endType')}
                  data={[
                    { value: 'ALL_RESULTS', label: 'All results' },
                    { value: 'FAILURES_ONLY', label: 'Failures only' },
                  ]}
                  allowDeselect={false}
                />
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </div>
      {!!showWeeklySummaryField && (
        <SummarySwitch
          form={form}
          field="weeklySummary"
          label="Weekly summary"
          description="Test run status from last week. Sends every Monday at 12:00 PM UTC (08:00 AM ET / 05:00 AM PT)"
        />
      )}
      {!!showWeeklySummaryField && (
        <SummarySwitch
          form={form}
          field="dailySummary"
          label="Daily summary"
          description="Snapshot of the last 24 hours. Sends daily at 12:00 PM UTC (08:00 AM ET / 05:00 AM PT)"
        />
      )}
    </div>
  );
}
